import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  IconButton,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Badge,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Paper,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { listProducts } from '../slices/productSlice';
import { fetchRootCategories } from '../slices/categorySlice';

function ValueProp({ icon, title, subtitle }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(25, 118, 210, 0.12)',
          flex: '0 0 auto',
          transition: 'all .2s ease',
          '&:hover': {
            bgcolor: 'primary.main',
            transform: 'scale(1.05)',
            '& svg': {
              color: 'white'
            }
          }
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function LandingPageTest() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = React.useState('');

  // Redux state
  const { userInfo } = useSelector((state) => state.user);
  const { items: cartItems } = useSelector((state) => state.cart);
  const { products } = useSelector((state) => state.productList);
  const { rootCategories } = useSelector((state) => state.categories);

  useEffect(() => {
    dispatch(listProducts({ keyword: '', pageNumber: 1, limit: 16 }));
    dispatch(fetchRootCategories());
  }, [dispatch]);

  // Get featured products (first 12)
  const featuredProducts = products?.slice(0, 12) || [];
  
  // Carousel settings
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 2,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 2,
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      }
    ]
  };
  
  // Map categories (use first 4 root categories)
  const displayCategories = rootCategories?.slice(0, 4) || [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      history.push(`/search/${searchTerm}`);
    }
  };

  const handleAddToCart = (productId) => {
    history.push(`/cart/${productId}?qty=1`);
  };

  const cartItemCount = cartItems?.reduce((acc, item) => acc + item.qty, 0) || 0;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Top Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Typography
              variant="h6"
              fontWeight={900}
              sx={{ 
                letterSpacing: 0.5, 
                cursor: 'pointer',
                transition: 'color .2s ease',
                '&:hover': { color: 'primary.main' }
              }}
              onClick={() => history.push('/')}
            >
              THE LOTUSTOR
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ width: { xs: 0, sm: 420 }, display: { xs: 'none', sm: 'block' } }}>
              <form onSubmit={handleSearch}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search products…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      transition: 'all .2s ease',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 2px 12px rgba(25,118,210,0.15)'
                      }
                    }
                  }}
                />
              </form>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                aria-label="account"
                onClick={() => history.push(userInfo ? '/profile' : '/login')}
                sx={{
                  transition: 'all .2s ease',
                  '&:hover': { 
                    bgcolor: 'action.hover',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <PersonOutlineIcon />
              </IconButton>
              <IconButton 
                aria-label="cart" 
                onClick={() => history.push('/cart')}
                sx={{
                  transition: 'all .2s ease',
                  '&:hover': { 
                    bgcolor: 'action.hover',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <Badge badgeContent={cartItemCount} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
              <Button
                variant="contained"
                sx={{ 
                  display: { xs: 'none', md: 'inline-flex' },
                  fontWeight: 700,
                  transition: 'all .2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(25,118,210,0.3)'
                  }
                }}
                onClick={() => history.push('/search')}
              >
                Shop Now
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                <Chip
                  label="Daily deals • Fast shipping • Smart finds"
                  sx={{ 
                    width: 'fit-content', 
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                />
                <Typography variant="h3" fontWeight={950} sx={{ lineHeight: 1.1 }}>
                  Discover modern essentials at prices you'll actually like.
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 17, maxWidth: 520, lineHeight: 1.6 }}>
                  Lotustor brings together trending tools, home upgrades, and tech accessories—curated
                  to be useful, giftable, and affordable.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                  <Button 
                    size="large" 
                    variant="contained" 
                    onClick={() => history.push('/search')}
                    sx={{ 
                      fontWeight: 700,
                      py: 1.5,
                      transition: 'all .2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(25,118,210,0.4)'
                      }
                    }}
                  >
                    Browse Best Sellers
                  </Button>
                  <Button 
                    size="large" 
                    variant="outlined" 
                    onClick={() => history.push('/')}
                    sx={{ 
                      fontWeight: 700,
                      py: 1.5,
                      transition: 'all .2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    Explore Categories
                  </Button>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ pt: 1 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<LocalShippingOutlinedIcon />}
                    label="Tracked shipping"
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    icon={<ReplayOutlinedIcon />} 
                    label="Easy returns" 
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    icon={<LockOutlinedIcon />} 
                    label="Secure checkout" 
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Categories */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 3 }}
        >
          <Typography variant="h5" fontWeight={900}>
            Shop by category
          </Typography>
          <Button 
            size="small" 
            onClick={() => history.push('/')}
            sx={{ 
              fontWeight: 700,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            View all
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {displayCategories.map((category) => (
            <Grid item xs={6} sm={4} md={2} key={category._id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'all .2s ease',
                  cursor: 'pointer',
                  '&:hover': { 
                    transform: 'translateY(-4px)', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)' 
                  },
                }}
                onClick={() => history.push(`/search?category=${category._id}`)}
              >
                <CardMedia
                  component="img"
                  height="100"
                  image={category.image || '/images/placeholder.jpg'}
                  alt={category.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography fontWeight={900} sx={{ mb: 0.5 }}>{category.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description || 'Browse products'}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button 
                    fullWidth 
                    variant="contained"
                    sx={{ fontWeight: 700 }}
                  >
                    Shop {category.name}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* Featured products */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 3 }}
        >
          <Typography variant="h5" fontWeight={900}>
            Featured picks
          </Typography>
          <Button 
            size="small" 
            onClick={() => history.push('/search')}
            sx={{ 
              fontWeight: 700,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            See more
          </Button>
        </Stack>

        <Box sx={{ 
          '& .slick-slide': { px: 1 },
          '& .slick-dots': { bottom: -40 },
          '& .slick-prev, & .slick-next': {
            zIndex: 1,
            '&:before': { fontSize: 30, color: 'primary.main' }
          },
          '& .slick-prev': { left: -35 },
          '& .slick-next': { right: -35 }
        }}>
          <Slider {...carouselSettings}>
          {featuredProducts.map((product) => (
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: 380,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all .2s ease',
                  '&:hover': { 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transform: 'translateY(-2px)' 
                  },
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="160"
                    image={product.image}
                    alt={product.name}
                    sx={{ 
                      cursor: 'pointer',
                      objectFit: 'cover',
                      transition: 'transform .3s ease',
                      '&:hover': { transform: 'scale(1.05)' }
                    }}
                    onClick={() => history.push(`/product/${product._id}`)}
                  />
                  {product.countInStock === 0 && (
                    <Chip
                      label="Out of Stock"
                      size="small"
                      color="error"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        left: 8, 
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 20,
                        backdropFilter: 'blur(4px)'
                      }}
                    />
                  )}
                  {product.countInStock > 0 && product.rating >= 4.5 && (
                    <Chip
                      label="Top Rated"
                      size="small"
                      color="primary"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        left: 8, 
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 20,
                        backdropFilter: 'blur(4px)'
                      }}
                    />
                  )}
                </Box>

                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1.5 }}>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      lineHeight: 1.2,
                      cursor: 'pointer',
                      mb: 1,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      '&:hover': { color: 'primary.main' },
                    }}
                    onClick={() => history.push(`/product/${product._id}`)}
                  >
                    {product.name}
                  </Typography>
                  <Box>
                    <Typography sx={{ mb: 0.3 }} fontWeight={900} variant="subtitle1">
                      ${product.price}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {product.rating} ⭐ ({product.numReviews})
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 1.5, pt: 0, gap: 0.5, flexDirection: 'column' }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    onClick={() => history.push(`/product/${product._id}`)}
                  >
                    View
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    disabled={product.countInStock === 0}
                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    onClick={() => handleAddToCart(product._id)}
                  >
                    Add to Cart
                  </Button>
                </CardActions>
              </Card>
          ))}
          </Slider>
        </Box>
      </Container>

      {/* Value props */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <ValueProp
                icon={<LocalShippingOutlinedIcon />}
                title="Fast shipping"
                subtitle="Reliable delivery with tracking on most orders."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ValueProp
                icon={<ReplayOutlinedIcon />}
                title="Easy returns"
                subtitle="Simple return process designed to be hassle-free."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ValueProp
                icon={<HeadsetMicOutlinedIcon />}
                title="Support that responds"
                subtitle="Quick help when you need it, without the runaround."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ValueProp
                icon={<LockOutlinedIcon />}
                title="Secure checkout"
                subtitle="Protected payments with modern security standards."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 5 }} />

          {/* Newsletter / CTA */}
          <Paper 
            variant="outlined" 
            sx={{ 
              borderRadius: 4, 
              p: { xs: 2.5, md: 4 },
              background: 'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0.02) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              transition: 'all .3s ease',
              '&:hover': {
                boxShadow: '0 8px 30px rgba(25,118,210,0.15)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" fontWeight={950}>
                  Get deals & new drops (no spam)
                </Typography>
                <Typography color="text.secondary">
                  Join the list for weekly discounts and trending finds.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField 
                    fullWidth 
                    placeholder="you@example.com"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper'
                      }
                    }}
                  />
                  <Button 
                    variant="contained" 
                    size="large"
                    sx={{ 
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      transition: 'all .2s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 4px 12px rgba(25,118,210,0.4)'
                      }
                    }}
                  >
                    Subscribe
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={950}>THE LOTUSTOR</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 420 }}>
                A curated marketplace for modern essentials—tools, tech, and home upgrades that
                deliver real value.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography fontWeight={900} sx={{ mb: 1 }}>
                Shop
              </Typography>
              <Stack spacing={0.75}>
                <Link
                  component={RouterLink}
                  to="/search"
                  underline="hover"
                  color="inherit"
                  sx={{ 
                    transition: 'all .2s ease',
                    '&:hover': { color: 'primary.main', pl: 0.5 }
                  }}
                >
                  Best Sellers
                </Link>
                <Link
                  component={RouterLink}
                  to="/search?sort=newest"
                  underline="hover"
                  color="inherit"
                  sx={{ 
                    transition: 'all .2s ease',
                    '&:hover': { color: 'primary.main', pl: 0.5 }
                  }}
                >
                  New Arrivals
                </Link>
                <Link
                  component={RouterLink}
                  to="/search?sort=price"
                  underline="hover"
                  color="inherit"
                  sx={{ 
                    transition: 'all .2s ease',
                    '&:hover': { color: 'primary.main', pl: 0.5 }
                  }}
                >
                  Deals
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography fontWeight={900} sx={{ mb: 1 }}>
                Support
              </Typography>
              <Stack spacing={0.75}>
                <Link
                  component={RouterLink}
                  to="/contactSupport"
                  underline="hover"
                  color="inherit"
                  sx={{ 
                    transition: 'all .2s ease',
                    '&:hover': { color: 'primary.main', pl: 0.5 }
                  }}
                >
                  Contact
                </Link>
                <Link 
                  component={RouterLink} 
                  to="/" 
                  underline="hover" 
                  color="inherit"
                  sx={{ 
                    transition: 'all .2s ease',
                    '&:hover': { color: 'primary.main', pl: 0.5 }
                  }}
                >
                  Shipping
                </Link>
                <Link 
                  component={RouterLink} 
                  to="/" 
                  underline="hover" 
                  color="inherit"
                  sx={{ 
                    transition: 'all .2s ease',
                    '&:hover': { color: 'primary.main', pl: 0.5 }
                  }}
                >
                  Returns
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={900} sx={{ mb: 1 }}>
                Trust
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={<LockOutlinedIcon />} label="Secure Payments" variant="outlined" />
                <Chip
                  icon={<LocalShippingOutlinedIcon />}
                  label="Tracked Delivery"
                  variant="outlined"
                />
                <Chip icon={<ReplayOutlinedIcon />} label="Easy Returns" variant="outlined" />
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} The Lotustor. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
